import { useAuth, getUserAvatarUrl } from '@/contexts/AuthContext';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { type Translate, useTranslation } from '@client-app/i18n';
import { featureService, WorkspaceInvitation } from '@common/api/featureService';
import { CircleNotch, WarningCircle } from '@phosphor-icons/react';
import React, { useRef, useState } from 'react';
import {
    buildInviteSuccessAdminRoute,
    resolveInviteAcceptWorkspaceId,
} from './inviteReturn';

interface InviteSelectionProps {
  invitations: WorkspaceInvitation[];
  onChooseCreate?: () => void;
  title?: string;
  subtitle?: string;
  createLabel?: string;
  hideCreateAction?: boolean;
  onAcceptingChange?: (isAccepting: boolean) => void;
}

const formatRoleName = (role: string | null | undefined, t?: Translate) => {
    const trimmedRole = String(role || '').trim();

    if (!trimmedRole) {
        return t ? t('website.membersSettings.memberRole') : 'Member';
    }

    const normalized = trimmedRole.toUpperCase();
    if (t) {
        if (normalized === 'OWNER') return t('website.membersSettings.ownerRole');
        if (normalized === 'ADMIN') return t('website.membersSettings.adminRole');
        if (normalized === 'MEMBER') return t('website.membersSettings.memberRole');
    }

    if (trimmedRole === trimmedRole.toUpperCase() || trimmedRole === trimmedRole.toLowerCase()) {
        return trimmedRole
            .replace(/[_-]+/g, ' ')
            .split(/\s+/)
            .filter(Boolean)
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
            .join(' ');
    }

    return trimmedRole;
};

const getRoleArticle = (roleName: string) => (
    /^[aeiou]/i.test(roleName.trim()) ? 'an' : 'a'
);

const InviteSelection: React.FC<InviteSelectionProps> = ({
    invitations,
    onChooseCreate,
    title,
    subtitle,
    createLabel,
    hideCreateAction = false,
    onAcceptingChange
}) => {
    const { t } = useTranslation();
    const { startOnboarding, stopOnboarding } = useOnboarding();
    const { user } = useAuth();
    const [acceptingId, setAcceptingId] = useState<string | null>(null);
    const acceptingIdRef = useRef<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const userAvatarUrl = getUserAvatarUrl(user);
    const isSingleInvitation = invitations.length === 1;
    const primaryInvitation = isSingleInvitation ? invitations[0] : null;
    const primaryInvitationRole = formatRoleName(
        primaryInvitation?.dataRoleName || primaryInvitation?.data_role_name || primaryInvitation?.role,
        t,
    );
    const primaryInvitationInviter = primaryInvitation?.inviterName || primaryInvitation?.invitedBy;
    const resolvedTitle = title || (
        isSingleInvitation
            ? t('website.onboarding.invite.joinWorkspaceTitle', {
                workspace: primaryInvitation?.workspaceName || t('website.onboarding.invite.thisWorkspace'),
              })
            : t('website.onboarding.invite.joinWorkspace')
    );
    const resolvedSubtitle = subtitle || (
        isSingleInvitation
            ? t('website.onboarding.invite.singleSubtitle', {
                inviter: primaryInvitationInviter || t('website.onboarding.invite.teammate'),
                article: getRoleArticle(primaryInvitationRole),
                role: primaryInvitationRole,
              })
            : t('website.onboarding.invite.multipleSubtitle', {
                count: invitations.length,
                plural: invitations.length > 1 ? 's' : '',
              })
    );

    const handleCreateWorkspace = () => {
        console.log('点击创建 workspace');
        // 通知父组件用户选择了创建 workspace
        onChooseCreate?.();
        // 开始 onboarding 流程
        startOnboarding('invite_selection');
    };

    const handleJoin = async (invitation: WorkspaceInvitation) => {
        if (acceptingIdRef.current) {
            return;
        }

        acceptingIdRef.current = invitation.id;
        setAcceptingId(invitation.id);
        setError(null);
        onAcceptingChange?.(true);
        let didStartNavigation = false;

        try {
            console.log('接受邀请:', invitation);

            // 调用接受邀请的 API
            const response = await featureService.acceptInvitation(invitation.token);

            console.log('接受邀请 API 响应:', response);

            const workspaceId = resolveInviteAcceptWorkspaceId(response);
            if (!workspaceId) {
                throw new Error(t('website.onboarding.invite.acceptFailed'));
            }

            stopOnboarding();
            window.location.assign(buildInviteSuccessAdminRoute(workspaceId, window.location.search));
            didStartNavigation = true;
            
        } catch (err: any) {
            console.error('接受邀请失败:', err);
            setError(err?.message || t('website.onboarding.invite.acceptFailed'));
            onAcceptingChange?.(false);
        } finally {
            if (!didStartNavigation) {
                acceptingIdRef.current = null;
                setAcceptingId(null);
            }
        }
    };

    if (invitations.length === 0) {
        return null;
    }

    return (
        <div className="w-full max-w-md">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="w-12 h-12 bg-gray-200 rounded-full mx-auto mb-4 overflow-hidden">
                    {/* User Avatar */}
                    <img
                        src={userAvatarUrl}
                        alt={t('website.onboarding.invite.userAvatarAlt')}
                        className="w-full h-full object-cover"
                    />
                </div>
                <h1 className="text-2xl font-bold text-text-main mb-2">
                    {resolvedTitle}
                </h1>
                <p className="text-text-sub">
                    {resolvedSubtitle}
                </p>
            </div>

            {/* Error Message */}
            {error ? (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                    <WarningCircle size={20} className="shrink-0" weight="bold" />
                    <p className="text-sm">{error}</p>
                </div>
            ) : null}

            <div className="mb-6">
                <div className="space-y-3">
                    {invitations.map((invite) => {
                        const inviteRoleName = formatRoleName(
                            invite.dataRoleName || invite.data_role_name || invite.role,
                        );
                        const inviteInviter = invite.inviterName || invite.invitedBy;

                        return (
                            <div
                                key={invite.id}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center justify-between gap-4 group hover:border-gray-300 transition-colors"
                            >
                                <div className="flex items-center gap-4 min-w-0">
                                    <div className="w-10 h-10 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-lg font-bold text-text-main shrink-0">
                                        {invite.workspaceName?.charAt(0).toUpperCase() || 'W'}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold text-text-main truncate">{invite.workspaceName}</div>
                                        {!isSingleInvitation && inviteInviter ? (
                                            <div className="text-sm text-text-sub truncate">
                                                {t('website.onboarding.invite.invitedBy')}
                                                {' '}
                                                {inviteInviter}
                                            </div>
                                        ) : null}
                                        <div className="text-sm text-text-sub truncate">
                                            {t('website.onboarding.invite.roleLabel')}
                                            {' '}
                                            {inviteRoleName}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleJoin(invite)}
                                    disabled={Boolean(acceptingId)}
                                    className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shrink-0"
                                >
                                    {acceptingId === invite.id ? (
                                        <>
                                            <CircleNotch size={16} className="animate-spin" weight="bold" />
                                            {t('website.onboarding.invite.joining')}
                                        </>
                                    ) : (
                                        t('website.onboarding.invite.join')
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Create Workspace Button */}
            {!hideCreateAction ? (
                <button
                    onClick={handleCreateWorkspace}
                    className="w-full py-2 bg-transparent border-0 text-text-sub hover:text-text-main text-sm font-normal transition-colors flex items-center justify-center gap-1.5 hover:underline cursor-pointer"
                >
                    {createLabel || t('website.onboarding.invite.createWorkspace')}
                </button>
            ) : null}
        </div>
    );
};

export default InviteSelection;
